import { motion } from "framer-motion";
import { Landmark, ArrowRight, XCircle, CheckCircle2 } from "lucide-react";
import { IMAGES, CULTURE_POINTS, COUNTRIES, RYDE_IMPACTS, FUNDING_TODAY, FUNDING_WITH_RG } from "@/data";
import TiltImage from "@/components/TiltImage";
import WhaleShark from "@/components/WhaleShark";
import ChapterMark from "@/components/ChapterMark";

export default function Culture() {
    return (
        <section id="culture" className="relative overflow-hidden scroll-mt-24 py-24 md:py-32" data-testid="culture-section">
            <div
                className="absolute inset-0 bg-cover bg-center opacity-[0.05] pointer-events-none"
                style={{ backgroundImage: `url(${IMAGES.balmoral})` }}
                aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#020D18] via-transparent to-[#020D18] pointer-events-none" aria-hidden="true" />
            <WhaleShark
                className="left-[-130px] bottom-[28%] w-[440px] -rotate-6"
                opacity={0.1}
                flip
                testid="culture-whale"
            />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                    <div>
                        <ChapterMark index="06" label="Caring for Country" testid="culture-eyebrow" />
                        <h2 className="font-display font-semibold tracking-tight text-4xl md:text-5xl" data-testid="culture-heading">
                            Restoring an enduring connection between community, culture and Country
                        </h2>
                        <div className="mt-8 flex flex-col gap-6" data-testid="culture-points">
                            {CULTURE_POINTS.map((point, i) => (
                                <motion.div
                                    key={point.title}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.45, delay: i * 0.08 }}
                                    className="border-l-2 border-sky-400/60 pl-5"
                                    data-testid={`culture-point-${i}`}
                                >
                                    <p className="text-lg font-semibold text-slate-100">{point.title}</p>
                                    <p className="mt-1 text-base text-slate-400 leading-relaxed">{point.detail}</p>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-10" data-testid="countries-block">
                            <p className="text-sm font-semibold tracking-[0.25em] uppercase text-slate-500 mb-4">
                                Countries of the catchment
                            </p>
                            <div className="flex flex-col gap-3">
                                {COUNTRIES.map((c, i) => (
                                    <motion.div
                                        key={c.group}
                                        initial={{ opacity: 0, x: -16 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true, margin: "-40px" }}
                                        transition={{ duration: 0.4, delay: i * 0.06 }}
                                        className="flex items-baseline gap-4 bg-[#05251C] border border-emerald-900/60 rounded-lg px-5 py-3 hover:border-emerald-400/50 transition-colors"
                                        data-testid={`country-chip-${i}`}
                                    >
                                        <span className="font-display font-semibold text-lg text-emerald-300 whitespace-nowrap">{c.group}</span>
                                        <span className="text-sm text-slate-400">{c.area}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-8 bg-[#05251C] border border-emerald-900/60 rounded-xl p-6" data-testid="culture-artists-highlight">
                            <p className="font-display font-semibold text-4xl text-emerald-300">1,106 tonnes</p>
                            <p className="mt-2 text-base text-slate-300 leading-relaxed">
                                of recyclable material allocated to Indigenous artists over ten years, 25% of
                                everything recyclable we pull from the river becomes raw material for culture,
                                not landfill.
                            </p>
                        </div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, x: 24 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6 }}
                        className="lg:sticky lg:top-24"
                    >
                        <div className="relative rounded-xl overflow-hidden border border-[#12283C]" data-testid="culture-image">
                            <TiltImage
                                src={IMAGES.art}
                                alt="Aboriginal rock art"
                                className="w-full h-[400px] object-cover"
                                testid="culture-image-tilt"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#020D18]/90 via-transparent to-transparent pointer-events-none" />
                            <div className="absolute bottom-0 p-6 pointer-events-none">
                                <p className="text-sm font-semibold tracking-[0.25em] uppercase text-sky-200">Commissioned hull artwork</p>
                                <p className="mt-2 text-base text-slate-300">
                                    Every Guardian hull is a commissioned canvas, a floating celebration of
                                    culture on the water it protects.
                                </p>
                            </div>
                        </div>

                        <div className="mt-6 bg-[#06182A] border border-[#12283C] rounded-xl p-6" data-testid="ryde-impact-card">
                            <div className="flex items-center gap-3">
                                <Landmark className="w-5 h-5 text-sky-200" />
                                <p className="font-display font-semibold text-lg">What pollution already costs Ryde</p>
                            </div>
                            <ul className="mt-4 flex flex-col gap-3">
                                {RYDE_IMPACTS.map((impact, i) => (
                                    <li key={i} className="flex gap-3 text-base text-slate-400 leading-relaxed" data-testid={`ryde-impact-${i}`}>
                                        <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-sky-200" />
                                        {impact}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </motion.div>
                </div>

                <div className="mt-20" data-testid="beaches-block">
                    <h3 className="font-display font-semibold text-3xl" data-testid="beaches-heading">
                        One harbour system, two very different beaches
                    </h3>
                    <p className="mt-3 text-lg text-slate-400 max-w-2xl leading-relaxed">
                        Photographed on location: the river foreshore at Putney today, and the standard the
                        eastern harbour already enjoys at Balmoral and Nielsen Park.
                    </p>
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="relative rounded-xl overflow-hidden border border-[#12283C] group" data-testid="beach-putney-card">
                            <img
                                src={IMAGES.foreshore}
                                alt="Littered Parramatta River foreshore at Putney with the city skyline behind"
                                className="w-full h-[340px] object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#020D18] via-[#020D18]/70 to-[#020D18]/10" />
                            <div className="absolute bottom-0 p-6">
                                <p className="text-sm font-semibold tracking-[0.25em] uppercase text-sky-200">Today, Putney Beach, Ryde</p>
                                <p className="mt-2 font-display font-semibold text-2xl">The river the funding was meant to fix</p>
                                <p className="mt-2 text-base text-slate-300 leading-relaxed">
                                    Photographed at Putney: the tide's daily deposit on the foreshore, within
                                    sight of the city, despite $697,000+ already committed to the Putney Park
                                    swimming site.
                                </p>
                            </div>
                        </div>
                        <div className="relative rounded-xl overflow-hidden border border-[#12283C] group" data-testid="beach-balmoral-card">
                            <img
                                src={IMAGES.balmoralReal}
                                alt="Aerial view of Balmoral Beach, Mosman, with clear turquoise water and moored boats"
                                className="w-full h-[340px] object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#020D18] via-[#020D18]/70 to-[#020D18]/10" />
                            <div className="absolute bottom-0 p-6">
                                <p className="text-sm font-semibold tracking-[0.25em] uppercase text-emerald-300">The standard, Balmoral Beach, Mosman</p>
                                <p className="mt-2 font-display font-semibold text-2xl">What clean water buys</p>
                                <p className="mt-2 text-base text-slate-300 leading-relaxed">
                                    Photographed at Balmoral: swimmable water that anchors tourism, recreation,
                                    business activity and property value. The river suburbs deserve the same.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="beach-gallery">
                        <div className="relative rounded-xl overflow-hidden border border-[#12283C] group" data-testid="beach-gallery-0">
                            <img
                                src={IMAGES.nielsen}
                                alt="Nielsen Park, Vaucluse, clear water over sandstone rock shelves"
                                className="w-full h-[260px] object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#020D18] via-transparent to-transparent" />
                            <div className="absolute bottom-0 p-4">
                                <p className="font-display font-semibold text-lg">Nielsen Park, Vaucluse</p>
                                <p className="text-sm text-slate-300">Shark-netted harbour swimming, photographed on location</p>
                            </div>
                        </div>
                        <div className="relative rounded-xl overflow-hidden border border-[#12283C] group" data-testid="beach-gallery-1">
                            <img
                                src={IMAGES.rubbish}
                                alt="Bottles, cans and packaging in the shallows at Putney Beach"
                                className="w-full h-[260px] object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#020D18] via-transparent to-transparent" />
                            <div className="absolute bottom-0 p-4">
                                <p className="font-display font-semibold text-lg">Putney Beach, the shallows today</p>
                                <p className="text-sm text-slate-300">Bottles, cans and packaging, photographed on the catchment</p>
                            </div>
                        </div>
                        <div className="relative rounded-xl overflow-hidden border border-[#12283C] group" data-testid="beach-gallery-2">
                            <img
                                src="/images/harbour-beach-vaucluse.jpg"
                                alt="A Vaucluse harbour beach with clear shallows and palms"
                                className="w-full h-[260px] object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#020D18] via-transparent to-transparent" />
                            <div className="absolute bottom-0 p-4">
                                <p className="font-display font-semibold text-lg">Vaucluse harbour foreshore</p>
                                <p className="text-sm text-slate-300">Clear shallows on the eastern harbour, photographed on location</p>
                            </div>
                        </div>
                    </div>
                    <p className="mt-3 text-sm text-slate-500" data-testid="beach-gallery-note">
                        All location photography on this page was taken in Sydney.
                    </p>
                </div>

                <div className="mt-20" data-testid="funding-block">
                    <p className="text-sm font-semibold tracking-[0.3em] uppercase text-sky-200 mb-4" data-testid="funding-eyebrow">
                        The Funding Question
                    </p>
                    <h3 className="font-display font-semibold text-3xl md:text-4xl" data-testid="funding-heading">
                        The same dollars, working upstream
                    </h3>
                    <p className="mt-4 text-lg text-slate-400 max-w-3xl leading-relaxed">
                        Councils already spend heavily on the symptoms, patch-ups, pumps and monitoring. A
                        RiverGuardian contract redirects that money to removing the source, every night, with
                        every kilogram accounted for.
                    </p>
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="relative rounded-xl overflow-hidden border border-[#12283C]" data-testid="funding-today-card">
                            <div
                                className="absolute inset-0 bg-cover bg-center opacity-30"
                                style={{ backgroundImage: "url(/images/harbour-bridge-pollution.jpg)" }}
                            />
                            <div className="absolute inset-0 bg-[#020D18]/70" />
                            <div className="relative p-7">
                                <p className="font-display font-semibold text-xl text-slate-100">Today, reactive spending</p>
                                <ul className="mt-4 flex flex-col gap-3">
                                    {FUNDING_TODAY.map((item, i) => (
                                        <li key={i} className="flex gap-3 text-base text-slate-300 leading-relaxed" data-testid={`funding-today-${i}`}>
                                            <XCircle className="w-4 h-4 mt-1 shrink-0 text-sky-200/70" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="relative rounded-xl overflow-hidden border border-emerald-900/60" data-testid="funding-rg-card">
                            <div
                                className="absolute inset-0 bg-cover bg-center opacity-30"
                                style={{ backgroundImage: `url(${IMAGES.beach})` }}
                            />
                            <div className="absolute inset-0 bg-[#05251C]/75" />
                            <div className="relative p-7">
                                <p className="font-display font-semibold text-xl text-emerald-300">With RiverGuardian, prevention</p>
                                <ul className="mt-4 flex flex-col gap-3">
                                    {FUNDING_WITH_RG.map((item, i) => (
                                        <li key={i} className="flex gap-3 text-base text-slate-200 leading-relaxed" data-testid={`funding-rg-${i}`}>
                                            <CheckCircle2 className="w-4 h-4 mt-1 shrink-0 text-emerald-300" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
